import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import "@/App.css";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import PracticeSelectPage from "./pages/PracticeSelectPage";
import PracticePage from "./pages/PracticePage";
import ResultsPage from "./pages/ResultsPage";
import DashboardPage from "./pages/DashboardPage";
import AdminPage from "./pages/AdminPage";
import ProfilePage from "./pages/ProfilePage";
import TrialPage from "./pages/TrialPage";
import TrialPaywallPage from "./pages/TrialPaywallPage";
import TrialPracticePage from "./pages/TrialPracticePage";
import TrialResultsPage from "./pages/TrialResultsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import OwnerSetupPage from "./pages/OwnerSetupPage";

// Smart API URL detection:
// - In development (localhost): use REACT_APP_BACKEND_URL from .env
// - In production (Emergent deployment): use same origin (frontend and backend share domain)
const getBackendUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  const currentOrigin = window.location.origin;
  
  // If running on localhost (development), use env URL
  if (currentOrigin.includes('localhost')) {
    return envUrl || 'http://localhost:8001';
  }
  
  // If running on Emergent preview domain, use env URL
  if (currentOrigin.includes('.preview.emergentagent.com') && envUrl) {
    return envUrl;
  }
  
  // For custom domains and production: use same origin
  // This ensures API calls go to domain.com/api/* instead of preview URL
  return currentOrigin;
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Debug logging
console.log("BACKEND_URL:", BACKEND_URL);
console.log("API:", API);

// Analytics tracking utilities
const getSessionId = () => {
  let sessionId = sessionStorage.getItem("analytics_session_id");
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem("analytics_session_id", sessionId);
  }
  return sessionId;
};

export const trackEvent = async (eventName, page = null, metadata = {}) => {
  try {
    const token = localStorage.getItem("token");
    await axios.post(
      `${API}/analytics/track`,
      {
        event_name: eventName,
        session_id: getSessionId(),
        page: page || window.location.pathname,
        metadata,
      },
      token ? { headers: { Authorization: `Bearer ${token}` } } : {}
    );
  } catch (err) {
    // Silently fail - analytics shouldn't break the app
    console.debug("Analytics track failed:", err.message);
  }
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem("token"));

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      setToken(null);
      localStorage.removeItem("token");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const updateMode = async (mode) => {
    await axios.put(
      `${API}/auth/mode`,
      { mode },
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      }
    );
    setUser((prev) => (prev ? { ...prev, mode } : null));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        token,
        login,
        register,
        logout,
        updateMode,
        checkAuth,
        isAuthenticated: !!user,
        isAdmin: user?.is_admin || user?.role === "ADMIN" || user?.role === "SUPER_ADMIN" || user?.role === "OWNER" || false,
        isSuperAdmin: user?.role === "SUPER_ADMIN" || user?.role === "OWNER" || false,
        isOwner: user?.role === "OWNER" || false,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component for Google OAuth
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const { checkAuth } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = location.hash;
      const sessionId = new URLSearchParams(hash.replace("#", "?")).get("session_id");

      if (sessionId) {
        try {
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );
          await checkAuth();
          navigate("/dashboard", { replace: true, state: { user: response.data } });
        } catch (error) {
          console.error("Session processing error:", error);
          navigate("/auth", { replace: true });
        }
      } else {
        navigate("/auth", { replace: true });
      }
    };

    processSession();
  }, [location.hash, navigate, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Completing sign in...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading, isAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in URL fragment (Google OAuth callback)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      {/* Password reset routes */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Internal/Owner setup - hidden page */}
      <Route path="/internal/owner-setup" element={<OwnerSetupPage />} />
      {/* Trial routes for guest users */}
      <Route path="/trial" element={<TrialPage />} />
      <Route path="/trial/paywall" element={<TrialPaywallPage />} />
      <Route path="/trial/practice/:passageId" element={<TrialPracticePage />} />
      <Route path="/trial/results/:attemptId" element={<TrialResultsPage />} />
      {/* Protected practice routes - require login */}
      <Route
        path="/practice"
        element={
          <ProtectedRoute>
            <PracticeSelectPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/practice/:passageId"
        element={
          <ProtectedRoute>
            <PracticePage />
          </ProtectedRoute>
        }
      />
      <Route path="/results/:attemptId" element={<ResultsPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            <AdminPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };
